import i18next from 'i18next'
import * as React from 'react'
import {
  StandardLocalWidget,
  any,
  async,
  fromJSX,
  loadedAsyncState,
  loadingAsyncState,
  nothing,
} from 'widgets-for-react'
import { getClassName } from '../../authentication/auth_widget/utils'
import { companyWidget } from '../../forms/company/companyWidget'
import { emptyCountry } from '../../forms/country/countryState'
import { paymentMethodWidget } from '../../forms/paymentMethod/paymentMethodWidget'
import { personalInfoWidget } from '../../forms/personalInfo/personalInfoWidget'
import { subscriptionSelectorWidget } from '../../forms/subscriptionSelector/subscriptionSelectorWidget'
import { authOptions } from '../../site/root/rootWidget'
import { DataLayer, initializePIIData, PIIData } from '../../utils/datalayer'
import {
  FormControls,
  FormSkeletonLayout,
  formSkeletonConfig,
} from '../../utils/forms'
import { ComponentDidMount } from '../../utils/lifecycleMethods'
import { capitalize } from '../../utils/utils'
import { singleValidatorResultBuilders } from '../../validator/validator'
import { SubscriptionLayout } from '../subscriptionLayout'
import { SubscriptionState, subscriptionUpdaters } from '../subscriptionState'
import { newSubscriptionInputUpdaters } from './newSubscriptionState'
import { EUCountriesAndUK, validateVAT } from './newSubscriptionUtils'

export const newSubscriptionWidget: StandardLocalWidget<SubscriptionState> = (
  currentState
) => {
  const lookupData = currentState.lookupData
  return currentState.kind != 'new subscription flow' || lookupData.kind !== "loaded"
    ? nothing()
    : any([
        async(currentState.newSubscriptionInput.checkVATState)
          .map(newSubscriptionInputUpdaters.checkVATState)
          .map(subscriptionUpdaters.newSubscriptionInput),
        currentState.subscribe.kind == 'loaded'
          ? fromJSX(() =>
              currentState.subscribe.kind == 'loaded' ? (
                currentState.subscribe.value.Kind == 'Redirect' ? (
                  <FormControls.Loader />
                ) : 
                currentState.subscribe.value.Kind == 'Success' ? (
                  <SubscriptionLayout.ThankYou
                    variant="subscription-success"
                    subscriptionState={currentState}
                  />
                ) : (
                  <SubscriptionLayout.FailureOrCancel variant="subscription-failure" />
                )
              ) : (
                <></>
              )
            )
          : fromJSX((setState) => {
              const { newSubscriptionInput, userSubscriptions } =
                currentState
              const { Currency, PaymentFrequency, Price } =
                newSubscriptionInput.subscriptionSelectorState.input
              const SelectedSubscriptionHeader = (
                <header
                  className={getClassName(
                    authOptions,
                    'headerClassName',
                    'mb-4'
                  )}
                >
                  <h1
                    className={getClassName(
                      authOptions,
                      'titleClassName',
                      'mb-4'
                    )}
                  >
                    {`${capitalize(
                      i18next.t(
                        `subscription:${PaymentFrequency?.toLowerCase()}`
                      )
                    )} ${i18next.t('subscription:subscription')}`}
                  </h1>
                  <p>
                    {`${Currency} ${Price} /${i18next.t(
                      `subscription:${
                        PaymentFrequency?.toLowerCase().includes('month')
                          ? 'month'
                          : 'year'
                      }`
                    )}`}
                  </p>
                </header>
              )

              const piiData = initializePIIData(currentState.userSubscriptions);

              return (
                <>
                  <ComponentDidMount
                    onMount={() => DataLayer.upgradeFlowStep1(true, piiData)}
                  />
                  <FormSkeletonLayout
                    type="trial-to-pro"
                    kind="text-image"
                    options={authOptions}
                    isClrInverted={true}
                    image={formSkeletonConfig.imageDark}
                    onSubmit={
                      newSubscriptionInput.step == 1
                        ? () =>
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.nextStep()
                              )
                            )
                        : newSubscriptionInput.step == 2
                        ? () =>
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.nextStep()
                              )
                            )
                        : newSubscriptionInput.step == 3
                        ? () =>
                            setState(subscriptionUpdaters.invokeSubscribeNowApi)
                        : undefined
                    }
                  >
                    {/* Step 1 */}
                    {newSubscriptionInput.step == 1 ? (
                      <>
                        <header
                          className={getClassName(
                            authOptions,
                            'headerClassName',
                            'mb-4'
                          )}
                        >
                          <h1
                            className={getClassName(
                              authOptions,
                              'titleClassName',
                              'mb-4'
                            )}
                          >
                            {i18next.t('subscription:TrialToProTitle')}
                          </h1>
                          <p
                            className={getClassName(
                              authOptions,
                              'introClassName',
                              'lead'
                            )}
                          >
                            {i18next.t('subscription:TrialToProIntro')}
                          </p>
                          <ul
                            className={
                              getClassName(
                                authOptions,
                                'introClassName',
                                'lead'
                              ) + ' list--hexagon'
                            }
                          >
                            <li className="list__item">
                              <p className="list__text">
                                {i18next.t(
                                  'paywall:paywallRegisterListPrimary'
                                )}
                              </p>
                            </li>
                            <li className="list__item">
                              <p className="list__text">
                                {i18next.t(
                                  'paywall:paywallRegisterListSecondary'
                                )}
                              </p>
                            </li>
                            <li className="list__item">
                              <p className="list__text">
                                {i18next.t(
                                  'paywall:paywallRegisterListTertiary'
                                )}
                              </p>
                            </li>
                          </ul>
                        </header>

                        <SubscriptionLayout.NewSubscriptionProgressBar
                          activeStep={newSubscriptionInput.step}
                        />

                        {lookupData.kind == 'loaded' ? (
                          subscriptionSelectorWidget(
                            lookupData.value.Subscriptions,
                            'trial-to-pro',
                            piiData
                          )(newSubscriptionInput.subscriptionSelectorState)
                            .map(
                              newSubscriptionInputUpdaters.subscriptionSelectorState
                            )
                            .map(subscriptionUpdaters.newSubscriptionInput)
                            .run(setState)
                        ) : (
                          <FormControls.Loader />
                        )}

                        <SubscriptionLayout.FormSkeletonActionAndBackButtons
                          action={() => {
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.nextStep()
                              )
                            )
                            DataLayer.upgradeFlowChangeStep(
                              1,
                              i18next.t('subscription:GoToStep2'),
                              'next'
                            )
                          }}
                          actionText={i18next.t('subscription:GoToStep2')}
                          actionDisabled={!newSubscriptionInput.isValid()}
                        />
                      </>
                    ) : // Step 2
                    newSubscriptionInput.step == 2 ? (
                      <>
                        {SelectedSubscriptionHeader}

                        <SubscriptionLayout.NewSubscriptionProgressBar
                          activeStep={newSubscriptionInput.step}
                        />

                        <div
                          className={getClassName(
                            authOptions,
                            'subHeaderClassName',
                            'mb-3'
                          )}
                        >
                          <h2
                            className={getClassName(
                              authOptions,
                              'subTitleClassName',
                              'mb-3'
                            )}
                          >
                            {i18next.t('subscription:PersonalInfo')}
                          </h2>
                        </div>

                        {lookupData.kind == 'loaded' ? (
                          personalInfoWidget(
                            undefined,
                            lookupData.value.Countries,
                            lookupData.value.Jobs,
                            false,
                            currentState.userSubscriptions.kind === 'loaded' &&
                              currentState.userSubscriptions.value.User
                                .Country !== null,
                            true,
                            false,
                            true
                          )(newSubscriptionInput.personalInfoState)
                            .map(newSubscriptionInputUpdaters.personalInfoState)
                            .map(subscriptionUpdaters.newSubscriptionInput)
                            .run(setState)
                        ) : (
                          <FormControls.Loader />
                        )}

                        <FormControls.FormRadioInGroup
                          name="subscriptionInvoiceAddress"
                          legend={i18next.t('subscription:InvoiceToCompany')}
                          required={true}
                          validation={singleValidatorResultBuilders.validating(
                            ''
                          )}
                          items={[
                            {
                              controlId: 'subscriptionInvoiceAddressDifferent',
                              label: i18next.t(
                                'subscription:InvoiceWithCompany'
                              ),
                              checked:
                                newSubscriptionInput.invoiceToCompany || false,
                              onChange: (newValue) =>
                                setState(
                                  subscriptionUpdaters.newSubscriptionInput(
                                    newSubscriptionInputUpdaters.invoiceToCompany(
                                      (_) => newValue
                                    )
                                  )
                                ),
                            },
                            {
                              controlId: 'subscriptionInvoiceAddressSame',
                              label: i18next.t(
                                'subscription:InvoiceWithoutCompany'
                              ),
                              checked:
                                !newSubscriptionInput.invoiceToCompany || false,
                              onChange: (newValue) =>
                                setState(
                                  subscriptionUpdaters.newSubscriptionInput(
                                    newSubscriptionInputUpdaters.invoiceToCompany(
                                      (_) => !newValue
                                    )
                                  )
                                ), // TODO use 'false'?
                            },
                          ]}
                        />

                        {newSubscriptionInput.invoiceToCompany ? (
                          lookupData.kind == 'loaded' &&
                          userSubscriptions.kind === 'loaded' ? (
                            companyWidget(
                              lookupData.value.Countries,
                              lookupData.value.CompanyTypes,
                              false,
                              userSubscriptions.value.User.Country !== null,
                              true,
                              undefined,
                              'upgrade',
                              currentState.newSubscriptionInput.checkVATState
                            )(newSubscriptionInput.companyState)
                              .map(newSubscriptionInputUpdaters.companyState)
                              .map(subscriptionUpdaters.newSubscriptionInput)
                              .run(setState)
                          ) : (
                            <FormControls.Loader />
                          )
                        ) : (
                          <></>
                        )}
                        <SubscriptionLayout.FormSkeletonActionAndBackButtons
                          back={() => {
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.prevStep()
                              )
                            )
                            DataLayer.upgradeFlowChangeStep(
                              2,
                              i18next.t('subscription:Back'),
                              'back'
                            )
                          }}
                          action={() => {
                            const countryId = currentState.newSubscriptionInput.companyState.country.input.Id
                            const availableCountries = lookupData.value.Countries
                            const country = availableCountries.find((s) => s.Id == countryId)?.Title || emptyCountry.Title || ''
                              
                            const VATnumber = currentState.newSubscriptionInput.companyState.input.VAT
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.checkVATState(() =>
                                currentState.newSubscriptionInput.invoiceToCompany && 
                                  country && EUCountriesAndUK.includes(country)
                                    ? loadingAsyncState(() =>
                                        validateVAT(
                                          countryId || 0,
                                          VATnumber || ''
                                        )
                                      )
                                    : loadedAsyncState(true)
                                )
                              )
                            )
                            DataLayer.upgradeFlowChangeStep(
                              2,
                              i18next.t('subscription:GoToStep3'),
                              'next'
                            )
                          }}
                          actionText={i18next.t('subscription:GoToStep3')}
                          actionDisabled={!newSubscriptionInput.isValid()}
                        />
                      </>
                    ) : // Step 3
                    newSubscriptionInput.step == 3 ? (
                      <>
                        {SelectedSubscriptionHeader}

                        <SubscriptionLayout.NewSubscriptionProgressBar
                          activeStep={newSubscriptionInput.step}
                        />

                        <div
                          className={getClassName(
                            authOptions,
                            'subHeaderClassName',
                            'mb-3'
                          )}
                        >
                          <h2
                            className={getClassName(
                              authOptions,
                              'subTitleClassName',
                              'mb-3'
                            )}
                          >
                            {i18next.t(
                              'subscription:SelectYourPreferredPaymentMethod'
                            )}
                          </h2>
                          <p
                            className={getClassName(
                              authOptions,
                              'introClassName',
                              'lead'
                            )}
                          >
                            {i18next.t(
                              'subscription:SelectYourPreferredPaymentMethodIntro',
                              {
                                period: PaymentFrequency
                                  ? PaymentFrequency.toLowerCase().includes(
                                      'month'
                                    )
                                    ? i18next.t('subscription:month')
                                    : i18next.t('subscription:year')
                                  : i18next
                                      .t('subscription:Step1')
                                      .toLowerCase(),
                              }
                            )}
                          </p>
                        </div>

                        {
                          // uncomment this to reuse a previous payment method
                          // currentState.userSubscriptions.kind == "loaded" && currentState.userSubscriptions.value.HasOriginalTransactionKey ?
                          //   <SubscriptionLayout.PaymentMethodStillValidAlert />
                          // :
                          lookupData.kind == 'loaded' ? (
                            paymentMethodWidget(lookupData.value.IDealIssuers)(
                              newSubscriptionInput.paymentMethodState
                            )
                              .map(
                                newSubscriptionInputUpdaters.paymentMethodState
                              )
                              .map(subscriptionUpdaters.newSubscriptionInput)
                              .run(setState)
                          ) : (
                            <FormControls.Loader />
                          )
                        }

                        {/* Terms & Conditions */}
                        <FormControls.FormCheckInGroup
                          controlId="termsAndConditions"
                          label={i18next.t('subscription:TermsAndConditions')}
                          required={true}
                          checked={
                            newSubscriptionInput.termsAndConditions || false
                          }
                          onChange={(b) =>
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.termsAndConditions(
                                  b
                                )
                              )
                            )
                          }
                          onBlur={() =>
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.termsAndConditions(
                                  newSubscriptionInput.termsAndConditions ||
                                    false
                                )
                              )
                            )
                          }
                          validation={
                            newSubscriptionInput.termsAndConditions === false
                              ? singleValidatorResultBuilders.failed(
                                  'termsAndConditions',
                                  i18next.t(
                                    'subscription:TermsAndConditionsError'
                                  )
                                )
                              : singleValidatorResultBuilders.passed(
                                  'termsAndConditions'
                                )
                          }
                        />

                        <SubscriptionLayout.FormSkeletonActionAndBackButtons
                          back={() => {
                            setState(
                              subscriptionUpdaters.newSubscriptionInput(
                                newSubscriptionInputUpdaters.prevStep()
                              )
                            )
                            DataLayer.upgradeFlowChangeStep(
                              3,
                              i18next.t('subscription:Back'),
                              'back'
                            )
                          }}
                          action={() => {
                            setState(subscriptionUpdaters.invokeSubscribeNowApi)
                            DataLayer.upgradeFlowChangeStep(
                              3,
                              i18next.t('subscription:SubscribeNow'),
                              'next'
                            )
                          }}
                          actionText={i18next.t('subscription:SubscribeNow')}
                          actionDisabled={!newSubscriptionInput.isValid()}
                        />
                      </>
                    ) : (
                      <></>
                    )}
                  </FormSkeletonLayout>
                </>
              )
            }),
      ])
    }
